// Ortak anahtar (switch) primitifi — iki portalda kullanılır: taşeron portalının yükleme
// modalındaki "Otomatik okuma" anahtarı (S19-D4, ilk çağıran) ve ortak personel düzenleme
// formunun "Aktif" alanı (#29, `personnel/PersonnelEditBody.tsx`; iki portalda da). Gerçek
// `<input type="checkbox" role="switch">`: klavye/erişilebilirlik yerel gelir (Space toggler,
// `aria-checked` native `checked`'tan türer), `role="switch"` yalnız AT'ye anahtar
// semantiğini bildirir — bir `<div onClick>` DEĞİL.
//
// OFF durumu DOLGU ile DEĞİL ÇERÇEVE (border-border-strong) ile taşınır — ölçülü karar:
// `surface` / `surface-muted` dolgu-farkı 1.36:1 (açık) / 1.16:1 (koyu) — GÖRÜNMEZ.
// `border-border-strong` yüzey üstünde AA 3:1'i geçer (tokens.css "input/kontrol
// kenarlığı" yorumu) — bkz. bu dosyanın altındaki JSDoc.
//
// ON dolgusu GRAFİT'tir (bg-fg), TURUNCU DEĞİL — AC25 "her durumda tek eleman kazanır,
// sıfır turuncu": turuncu YALNIZ "bu, alınacak aksiyon" demektir (yükleme modalında sayfanın
// `Belge Yükle`'si — footer'daki `Tamam` bilerek `secondary` —, personel formunda `Kaydet`);
// anahtar da turuncu olursa o aksiyonla yarışır (düzeltme: tasarım review §7.2/§13).

import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from './cn';

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  /** Anahtarın altında yardımcı metin (ör. kapalıyken uyarı satırı). */
  description?: ReactNode;
  /** Dış sarmalayıcıya (label + anahtar + açıklama) uygulanır — track/thumb'a değil. */
  className?: string;
}

/**
 * Kontrollü anahtar (checked/onChange her zaman çağırandan gelir — DocumentUploadModal'daki
 * `register()` alanlarıyla aynı ruhta, ama boolean state RHF `Controller` ile veya düz
 * `useState` ile beslenebilir; component kendi state'ini TUTMAZ).
 *
 * Vurgu taşımaz: ON dolgusu (`bg-fg` — grafit) durumu bildirir, bir CTA DEĞİLDİR — footer'daki
 * `Button`'un `primary`/`secondary` dengesiyle YARIŞMAZ (tasarım review §7.2/§13, AC25
 * "sıfır turuncu"). Çağıran, boyut/konum dışında bir "vurgu" prop'u BEKLEMEMELİDİR — bilerek
 * yok; turuncu bu component'e ASLA girmez.
 *
 * ON  — border-fg + bg-fg (thumb bg-surface) — açık 15.68:1 · koyu 13.50:1 (fg/surface,
 *       tokens.css'ten ölçüldü — thumb her iki temada da net okunur)
 * OFF — border-border-strong + bg-surface (thumb bg-border-strong) — açık 3.79:1 · koyu 3.40:1
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { checked, onChange, label, description, className, disabled, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = description ? `${inputId}-hint` : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-center gap-3',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        )}
      >
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-checked={checked}
          aria-describedby={hintId}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        {/* Görsel track — dolgu YALNIZ ON'da anlam taşır; OFF çerçeveden okunur. */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none relative h-6 w-11 shrink-0 rounded-full border transition-colors',
            'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface',
            checked ? 'border-fg bg-fg' : 'border-border-strong bg-surface',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 left-0.5 h-5 w-5 rounded-full shadow transition-transform',
              checked ? 'translate-x-5 bg-surface' : 'translate-x-0 bg-border-strong',
            )}
          />
        </span>
        <span className="text-sm font-medium text-fg">{label}</span>
      </label>
      {description ? (
        <p id={hintId} className="text-xs text-muted">
          {description}
        </p>
      ) : null}
    </div>
  );
});
